import React, { useState, useEffect } from 'react';
import { X, Calendar, Clock, MapPin, User, Search, ClipboardList, RefreshCw, XCircle, CreditCard, CheckCircle } from 'lucide-react';
import { apiFetch } from '../../utils/apiClient';

const MyAppointmentsModal = ({ isOpen, onClose, onSelectAppointment, onAppointmentDetailsSelect, prefilledUHID = '', selectedAppointmentId = '', selectedAction = '', currentUser }) => {
  const [uhid, setUhid] = useState('');
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState('');
  const [userUHIDs, setUserUHIDs] = useState([]);

  // Fetch user's UHIDs when modal opens (for non-admin users)
  useEffect(() => {
    if (isOpen && currentUser && !currentUser.is_admin && currentUser.phoneNumber) {
      const fetchUserUHIDs = async () => {
        try {
          const cleanedPhone = currentUser.phoneNumber.replace(/\D/g, '');

          // First, try to get patients registered BY this user
          try {
            const registeredByResponse = await apiFetch(`/backend/api/patients/registered-by/${cleanedPhone}`, {
              method: 'GET'
            });

            if (registeredByResponse.status === 401) {
              console.warn('Session expired while fetching registered patients.');
              return;
            }

            if (registeredByResponse.ok) {
              const registeredByData = await registeredByResponse.json();

              if (registeredByData.success && registeredByData.allowedPhoneNumbers && registeredByData.allowedPhoneNumbers.length > 0) {
                // Get all UHIDs for all phone numbers registered by this user
                const allUHIDs = [];
                for (const phone of registeredByData.allowedPhoneNumbers) {
                  const response = await apiFetch(`/backend/api/patients/phone/${phone}`, {
                    method: 'GET'
                  });

                  if (!response.ok) {
                    continue;
                  }

                  const data = await response.json();
                  if (data.success && data.patients) {
                    allUHIDs.push(...data.patients.map(p => p.uhid));
                  }
                }

                // If we found UHIDs from patients registered by this user, use those
                if (allUHIDs.length > 0) {
                  setUserUHIDs(allUHIDs);
                  return;
                }
              }
            }
          } catch (err) {
            console.log('No patients registered by this user');
          }

          // Also check for patients with the same phone number as the logged-in user
          const response = await apiFetch(`/backend/api/patients/phone/${cleanedPhone}`, {
            method: 'GET'
          });

          if (!response.ok) {
            return;
          }

          const data = await response.json();
          if (data.success && data.patients) {
            setUserUHIDs(data.patients.map(p => p.uhid));
          }
        } catch (err) {
          console.error('Error fetching user UHIDs:', err);
        }
      };
      fetchUserUHIDs();
    }
  }, [isOpen, currentUser]);

  // Auto-fill UHID when modal opens with prefilledUHID
  useEffect(() => {
    if (isOpen && prefilledUHID) {
      setUhid(prefilledUHID);
    }
  }, [isOpen, prefilledUHID]);

  const handleSearch = async (e) => {
    e.preventDefault();

    console.log('Current User:', currentUser); // Debug log
    console.log('User UHIDs:', userUHIDs); // Debug log
    console.log('Searching for UHID:', uhid.trim()); // Debug log
    console.log('Prefilled UHID:', prefilledUHID); // Debug log

    if (!uhid.trim()) {
      setError('Please enter a UHID');
      return;
    }

    // Check if user is trying to access their own data (unless admin)
    if (currentUser && !currentUser.is_admin) {
      // If this UHID was pre-filled (selected from another part of the app), allow it
      if (prefilledUHID && uhid.trim() === prefilledUHID) {
        console.log('Allowing search for pre-filled UHID');
        // Allow search to continue for pre-filled UHIDs
      } else if (userUHIDs.length === 0) {
        // Only block if no UHIDs found AND it's not a pre-filled UHID
        console.warn('No UHIDs found for user and not a pre-filled UHID');
        // Don't block - let them try to search, the backend will handle authorization
      } else if (userUHIDs.length > 0 && !userUHIDs.includes(uhid.trim())) {
        // Only block if we have a list of UHIDs and this one isn't in it
        setError(`⚠️ Access Denied: You can only search for your registered UHID. Your UHIDs: ${userUHIDs.join(', ')}`);
        setAppointments([]);
        setSearched(false);
        return;
      }
    }

    setLoading(true);
    setError('');
    setSearched(true);

    try {
      const response = await apiFetch(`/backend/api/appointments/patient/${uhid}`, {
        method: 'GET'
      });

      if (response.status === 401) {
        setError('Your session has expired. Please log in again to view appointments.');
        setAppointments([]);
        return;
      }

      if (!response.ok) {
        setError(`Failed to fetch appointments (status ${response.status})`);
        setAppointments([]);
        return;
      }

      const data = await response.json();

      if (data.success) {
        setAppointments(data.appointments);
        if (data.appointments.length === 0) {
          setError('No appointments found for this UHID');
        }
      } else {
        setError(data.error || 'Failed to fetch appointments');
        setAppointments([]);
      }
    } catch (err) {
      setError('Error connecting to server. Please try again.');
      setAppointments([]);
      console.error('Error fetching appointments:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'rescheduled':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleClose = () => {
    setUhid('');
    setAppointments([]);
    setSearched(false);
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <ClipboardList className="w-6 h-6" />
              <h2 className="text-xl font-semibold">My Appointments</h2>
            </div>
            <button
              onClick={handleClose}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Selected UHID Banner */}
          {prefilledUHID && (
            <div className="mb-4 bg-gradient-to-r from-green-50 to-green-100 border-2 border-green-300 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center shadow-md flex-shrink-0">
                  <CreditCard className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-green-600 font-semibold uppercase tracking-wider">Auto-Selected UHID</p>
                  <p className="text-lg font-mono font-bold text-green-700">{prefilledUHID}</p>
                </div>
                <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
              </div>
              <p className="mt-2 text-xs text-green-600 text-center">
                Search field auto-filled - click Search to view appointments
              </p>
            </div>
          )}

          {/* Search Form */}
          <form onSubmit={handleSearch} className="mb-6">
            <div className="flex space-x-3">
              <input
                type="text"
                value={uhid}
                onChange={(e) => setUhid(e.target.value)}
                placeholder="Enter your UHID or select from Find My UHID"
                className={`flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent ${
                  prefilledUHID
                    ? 'border-green-400 bg-green-50 text-green-800 font-semibold focus:ring-green-500'
                    : 'border-gray-300 focus:ring-purple-500'
                }`}
                disabled={loading}
              />
              <button
                type="submit"
                disabled={loading}
                className={`px-6 py-2 rounded-lg transition-colors flex items-center space-x-2 ${
                  loading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-purple-600 hover:bg-purple-700 text-white'
                }`}
              >
                <Search className="w-5 h-5" />
                <span>{loading ? 'Searching...' : 'Search'}</span>
              </button>
            </div>
            {error && (
              <p className="mt-2 text-sm text-red-600">{error}</p>
            )}
          </form>

          {/* Appointments List */}
          {searched && !loading && (
            <div>
              {appointments.length > 0 ? (
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-700 mb-3">
                    Found {appointments.length} appointment{appointments.length > 1 ? 's' : ''}
                  </h3>
                  {appointments.map((appointment, index) => (
                    <div
                      key={appointment.appointment_id || index}
                      className={`border-2 rounded-lg p-5 transition-all ${
                        appointment.status === 'cancelled'
                          ? 'border-red-300 bg-red-50 opacity-75'
                          : 'border-purple-200 bg-white'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-purple-600 rounded-full flex items-center justify-center shadow-md">
                              <Calendar className="w-6 h-6 text-white" />
                            </div>
                            <div>
                              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Appointment ID</span>
                              <p className="font-mono text-xl font-bold text-purple-600">{appointment.appointment_id}</p>
                            </div>
                          </div>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold uppercase ${getStatusColor(appointment.status)}`}>
                          {appointment.status === 'cancelled' && '🚫 '}
                          {appointment.status || 'Pending'}
                        </span>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="flex items-center space-x-2">
                          <MapPin className="w-4 h-4 text-gray-400" />
                          <div>
                            <span className="text-xs text-gray-500">Hospital</span>
                            <p className="text-sm font-medium">{appointment.hospital}</p>
                          </div>
                        </div>

                        <div className="flex items-center space-x-2">
                          <User className="w-4 h-4 text-gray-400" />
                          <div>
                            <span className="text-xs text-gray-500">Doctor</span>
                            <p className="text-sm font-medium">{appointment.doctor}</p>
                          </div>
                        </div>

                        <div className="flex items-center space-x-2">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          <div>
                            <span className="text-xs text-gray-500">Date</span>
                            <p className="text-sm font-medium">{appointment.date || formatDate(appointment.createdAt)}</p>
                          </div>
                        </div>

                        <div className="flex items-center space-x-2">
                          <Clock className="w-4 h-4 text-gray-400" />
                          <div>
                            <span className="text-xs text-gray-500">Time Slot</span>
                            <p className="text-sm font-medium">{appointment.slot}</p>
                          </div>
                        </div>
                      </div>

                      {appointment.status === 'cancelled' && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                            <div className="flex items-center gap-2 text-red-700">
                              <XCircle className="w-5 h-5" />
                              <p className="text-sm font-semibold">This appointment has been cancelled</p>
                            </div>
                            {appointment.cancelledAt && (
                              <p className="text-xs text-red-600 mt-1">
                                Cancelled on: {formatDateTime(appointment.cancelledAt)}
                              </p>
                            )}
                            <p className="text-xs text-gray-600 mt-2">
                              No further actions can be taken on this appointment.
                            </p>
                          </div>
                        </div>
                      )}

                      {appointment.status === 'rescheduled' && appointment.previousDate && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <p className="text-xs text-gray-500">
                            Previously scheduled: {appointment.previousDate} - {appointment.previousSlot}
                          </p>
                        </div>
                      )}

                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <p className="text-xs text-gray-500">
                          Booked on: {formatDateTime(appointment.createdAt)}
                        </p>
                      </div>

                      {/* Show if this appointment is currently selected */}
                      {selectedAppointmentId === appointment.appointment_id && selectedAction && (
                        <div className={`mt-3 pt-3 border-t border-gray-200 ${
                          selectedAction === 'reschedule' ? 'bg-orange-50' : 'bg-red-50'
                        } -mx-5 px-5 pb-3`}>
                          <div className="flex items-center justify-center gap-2">
                            <CheckCircle className={`w-5 h-5 ${
                              selectedAction === 'reschedule' ? 'text-orange-600' : 'text-red-600'
                            }`} />
                            <p className={`text-sm font-semibold ${
                              selectedAction === 'reschedule' ? 'text-orange-700' : 'text-red-700'
                            }`}>
                              Selected for {selectedAction === 'reschedule' ? 'Reschedule' : 'Cancellation'}
                            </p>
                          </div>
                          <p className="text-xs text-gray-600 text-center mt-1">
                            Go to Book Appointment to continue
                          </p>
                        </div>
                      )}

                      {/* Action Buttons - Only show for non-cancelled appointments */}
                      {/* If cancelled: hide all actions. If rescheduled or confirmed: show Reschedule and Cancel */}
                      {appointment.status !== 'cancelled' && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          {/* Selection Button */}
                          {selectedAppointmentId !== appointment.appointment_id && (
                            <div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (onSelectAppointment) {
                                    onSelectAppointment(appointment.appointment_id, '');
                                  }
                                }}
                                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white rounded-lg transition-all shadow-md hover:shadow-lg transform hover:scale-105 font-semibold"
                              >
                                <CheckCircle className="w-5 h-5" />
                                Select This Appointment
                              </button>
                              <p className="text-xs text-gray-500 text-center mt-2">
                                Click to select this appointment, then choose an action
                              </p>
                            </div>
                          )}

                          {/* Action Buttons - Only show when appointment is selected */}
                          {selectedAppointmentId === appointment.appointment_id && (
                            <div>
                              <div className="mb-3 bg-purple-50 border-2 border-purple-300 rounded-lg p-3">
                                <div className="flex items-center justify-center gap-2">
                                  <CheckCircle className="w-5 h-5 text-purple-600" />
                                  <p className="text-sm font-semibold text-purple-700">
                                    Appointment Selected
                                  </p>
                                </div>
                                <p className="text-xs text-gray-600 text-center mt-1">
                                  Choose an action below
                                </p>
                              </div>

                              <div className="flex gap-3">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (onSelectAppointment) {
                                      onSelectAppointment(appointment.appointment_id, 'reschedule');
                                    }
                                  }}
                                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg transition-all shadow-md hover:shadow-lg transform hover:scale-105 font-semibold ${
                                    selectedAction === 'reschedule'
                                      ? 'bg-gradient-to-r from-orange-600 to-orange-700 text-white ring-2 ring-orange-400'
                                      : 'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white'
                                  }`}
                                >
                                  <RefreshCw className="w-4 h-4" />
                                  {selectedAction === 'reschedule' ? 'Reschedule Selected' : 'Reschedule'}
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (onSelectAppointment) {
                                      onSelectAppointment(appointment.appointment_id, 'cancel');
                                    }
                                  }}
                                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg transition-all shadow-md hover:shadow-lg transform hover:scale-105 font-semibold ${
                                    selectedAction === 'cancel'
                                      ? 'bg-gradient-to-r from-red-600 to-red-700 text-white ring-2 ring-red-400'
                                      : 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white'
                                  }`}
                                >
                                  <XCircle className="w-4 h-4" />
                                  {selectedAction === 'cancel' ? 'Cancel Selected' : 'Cancel'}
                                </button>
                              </div>

                              <p className="text-xs text-gray-500 text-center mt-2">
                                Go to Book Appointment to continue with your action
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <ClipboardList className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No appointments found for this UHID</p>
                  <p className="text-sm text-gray-400 mt-2">Please check your UHID and try again</p>
                </div>
              )}
            </div>
          )}

          {/* Initial State */}
          {!searched && !loading && (
            <div className="text-center py-12">
              <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Enter your UHID to view appointments</p>
              <p className="text-sm text-gray-400 mt-2">Your UHID was provided during registration</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyAppointmentsModal;